import React, {useEffect, useState} from "react";
import {
  Box, Button, ButtonBase, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, FormControlLabel,
  IconButton, Stack, Switch, TextField, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Cancel01Icon, HelpCircleIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {WhatsappConnectorRepository} from "../../../repositories/WhatsappConnectorRepository";
import useConfirm from "../../../hooks/useConfirm";

const EMPTY_FORM = {name: "", enabled: true, phoneNumberId: "", businessAccountId: "", accessToken: ""};

// Meta's IDs are digits only
const isDigits = (value) => /^\d+$/.test(value.trim());

const sectionTitleSx = {fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0", mb: 1};
const buttonSx = (variant) => ({
  height: 38, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
  ...(variant === "dark" && {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"}}),
  ...(variant === "light" && {color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"}}),
  ...(variant === "danger" && {color: "#C73A3A", "&:hover": {bgcolor: "#FDECEC"}}),
});

const META_APPS_URL = "https://developers.facebook.com/apps";
const SYSTEM_USERS_URL = "https://business.facebook.com/settings/system-users";

const HelpLink = ({href, children}) => (
  <Box component={"a"} href={href} target={"_blank"} rel={"noopener noreferrer"}
       sx={{color: "#4B55C9", fontWeight: 600, textDecoration: "none", "&:hover": {textDecoration: "underline"}}}>
    {children}
  </Box>
);

const HelpStep = ({number, title, children}) => (
  <Stack direction={"row"} gap={1.25}>
    <Box sx={{
      width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)",
    }}>
      {number}
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{title}</Typography>
      <Typography component={"div"} sx={{fontSize: 12.5, color: "#4A4A63", lineHeight: 1.55}}>{children}</Typography>
    </Box>
  </Stack>
);

// Where the phone number ID, business account ID and a permanent access token are in Meta
const ConnectHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{borderRadius: "14px", border: "1px solid #DDE1FB", bgcolor: "#F6F7FE"}}>
      <ButtonBase onClick={() => setOpen(value => !value)} aria-expanded={open} sx={{
        width: "100%", justifyContent: "flex-start", gap: 1, px: 1.75, py: 1.25, borderRadius: "14px",
        fontSize: 13, fontWeight: 600, color: "#4B55C9",
      }}>
        <HugeiconsIcon icon={HelpCircleIcon} size={16}/>
        Where do I find the phone number ID and access token?
        <Box sx={{ml: "auto", display: "flex", transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none"}}>
          <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
        </Box>
      </ButtonBase>
      <Collapse in={open}>
        <Stack gap={1.5} sx={{px: 1.75, pb: 1.75}}>
          <HelpStep number={1} title={"Open your app in Meta for Developers"}>
            Go to <HelpLink href={META_APPS_URL}>developers.facebook.com/apps</HelpLink> and open your app (type Business).
            If you don't have one, create it and add the <b>WhatsApp</b> product.
          </HelpStep>
          <HelpStep number={2} title={"Copy the phone number ID"}>
            In the left menu open <b>WhatsApp → API Setup</b>. Under the <b>From</b> number you will see the{" "}
            <b>Phone number ID</b> and the <b>WhatsApp Business Account ID</b>. Copy both here.
          </HelpStep>
          <HelpStep number={3} title={"Create a permanent access token"}>
            The token on the API Setup page expires after 24 hours, so use a system user token instead. Open{" "}
            <HelpLink href={SYSTEM_USERS_URL}>Business settings → System users</HelpLink>, add a system user with the
            Admin role, then <b>Assign assets</b>: your app and your WhatsApp account (full control).
          </HelpStep>
          <HelpStep number={4} title={"Generate the token"}>
            Click <b>Generate new token</b>, pick your app, set the expiration to <b>Never</b> and select the
            permissions <b>whatsapp_business_messaging</b> and <b>whatsapp_business_management</b>. Copy the token and
            paste it here. Meta shows it only once.
          </HelpStep>
          <HelpStep number={5} title={"Testing with Meta's test number?"}>
            While the app is in development, messages are only delivered to numbers added to the <b>To</b> list on the
            API Setup page. Add your own phone there before sending a test message.
          </HelpStep>
        </Stack>
      </Collapse>
    </Box>
  );
};

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;

// Connects the project's WhatsApp Cloud API number, or edits / removes the connected one. The access token is
// write-only: it is never loaded, the field only says whether one is saved.
// connectorId: the connector to edit, null to create one. onSaved(): after a create, edit or delete.
export const WhatsappConnectorDialog = ({open, projectId, connectorId, onClose, onSaved}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [accessTokenSet, setAccessTokenSet] = useState(false);
  // filled by the backend from Meta when the number is saved
  const [metaInfo, setMetaInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = useConfirm();
  const isEdit = Boolean(connectorId);
  const busy = saving || deleting;

  useEffect(() => {
    if (!open) return;
    setAccessTokenSet(false);
    setMetaInfo(null);
    if (!connectorId) {
      setForm(EMPTY_FORM);
      return;
    }
    let cancelled = false;
    setLoading(true);
    WhatsappConnectorRepository.get(connectorId)
      .then(({data}) => {
        if (cancelled) return;
        setForm({
          ...EMPTY_FORM,
          name: data.name || "",
          enabled: data.enabled !== false,
          phoneNumberId: data.phoneNumberId || "",
          businessAccountId: data.businessAccountId || "",
        });
        setAccessTokenSet(Boolean(data.accessTokenSet));
        setMetaInfo({displayPhoneNumber: data.displayPhoneNumber, verifiedName: data.verifiedName});
      })
      .catch(error => {
        if (cancelled) return;
        toast.error(errorMessage(error, "Failed to load the WhatsApp connector"));
        onClose();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, connectorId, onClose]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(previous => ({...previous, [key]: value}));
  };

  const phoneNumberIdError = Boolean(form.phoneNumberId.trim()) && !isDigits(form.phoneNumberId);
  const businessAccountIdError = Boolean(form.businessAccountId.trim()) && !isDigits(form.businessAccountId);
  // a token is required until one is saved
  const valid = form.name.trim() && form.phoneNumberId.trim() && !phoneNumberIdError && !businessAccountIdError
    && (accessTokenSet || form.accessToken.trim());

  const save = () => {
    const body = {
      name: form.name.trim(),
      enabled: form.enabled,
      phoneNumberId: form.phoneNumberId.trim(),
      businessAccountId: form.businessAccountId.trim() || undefined,
      accessToken: form.accessToken.trim() || undefined,
    };
    setSaving(true);
    (isEdit ? WhatsappConnectorRepository.edit(connectorId, body) : WhatsappConnectorRepository.create({...body, projectId}))
      .then(() => {
        toast.success(isEdit ? "WhatsApp connector updated" : "WhatsApp connected");
        onSaved();
        onClose();
      })
      .catch(error => toast.error(errorMessage(error, isEdit ? "Failed to update the WhatsApp connector" : "Failed to connect WhatsApp")))
      .finally(() => setSaving(false));
  };

  const remove = async () => {
    const confirmed = await confirmDelete({
      title: "Disconnect WhatsApp?",
      message: "The project can't send WhatsApp messages until you connect a number again. The saved access token is deleted.",
      confirmText: "Disconnect",
    });
    if (!confirmed) return;
    setDeleting(true);
    WhatsappConnectorRepository.delete(connectorId)
      .then(() => {
        toast.success("WhatsApp disconnected");
        onSaved();
        onClose();
      })
      .catch(error => toast.error(errorMessage(error, "Failed to disconnect WhatsApp")))
      .finally(() => setDeleting(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth={"sm"}
            PaperProps={{sx: {borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)"}}}>
      <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} sx={{px: 3, pt: 2.5, pb: 1}}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>WHATSAPP CONNECTOR</Typography>
          <Typography sx={{fontSize: 22, fontWeight: 700, color: "#23264D"}}>
            {isEdit ? "Edit WhatsApp number" : "Connect WhatsApp"}
          </Typography>
          <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>
            From your app in Meta for Developers → WhatsApp → API Setup
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busy} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>

      {loading ? (
        <Stack alignItems={"center"} gap={1} sx={{py: 6}}>
          <CircularProgress size={30} thickness={5} sx={{color: "#8491F7"}}/>
          <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>Loading WhatsApp number…</Typography>
        </Stack>
      ) : (
        <DialogContent sx={{px: 3, display: "flex", flexDirection: "column", gap: 2.5}}>
          <ConnectHelp/>

          {(metaInfo?.displayPhoneNumber || metaInfo?.verifiedName) && (
            <Box>
              <Typography sx={sectionTitleSx}>FROM WHATSAPP</Typography>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <TextField label={"Phone number"} size={"small"} fullWidth disabled value={metaInfo.displayPhoneNumber || "—"}/>
                <TextField label={"Verified name"} size={"small"} fullWidth disabled value={metaInfo.verifiedName || "—"}/>
              </Stack>
            </Box>
          )}

          <Box>
            <Typography sx={sectionTitleSx}>CONNECTOR</Typography>
            <TextField label={"Connector name"} size={"small"} fullWidth required value={form.name} onChange={set("name")}
                       autoComplete={"off"} placeholder={"e.g. Support"}/>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>WHATSAPP CLOUD API</Typography>
            <Stack gap={1.5}>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <TextField label={"Phone number ID"} size={"small"} fullWidth required value={form.phoneNumberId}
                           onChange={set("phoneNumberId")} autoComplete={"off"} placeholder={"e.g. 106540352242922"}
                           error={phoneNumberIdError} helperText={phoneNumberIdError ? "Digits only" : " "}/>
                <TextField label={"WhatsApp Business account ID"} size={"small"} fullWidth value={form.businessAccountId}
                           onChange={set("businessAccountId")} autoComplete={"off"}
                           error={businessAccountIdError} helperText={businessAccountIdError ? "Digits only" : "Optional"}/>
              </Stack>
              <TextField
                label={"Access token"}
                size={"small"}
                fullWidth
                required={!accessTokenSet}
                type={"password"}
                value={form.accessToken}
                onChange={set("accessToken")}
                autoComplete={"new-password"}
                placeholder={accessTokenSet ? "••••••••••••" : "EAAG…"}
                InputLabelProps={accessTokenSet ? {shrink: true} : undefined}
                helperText={accessTokenSet
                  ? "A token is saved and never shown. Leave empty to keep it, or paste a new one to replace it."
                  : "Use a permanent token of a system user. It is stored encrypted and never shown again."}
              />
              <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                The phone number ID and token are checked with WhatsApp when you save.
              </Typography>
            </Stack>
          </Box>

          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={set("enabled")}/>}
            label={<Typography sx={{fontSize: 13, color: "#4A4A63"}}>Send project WhatsApp messages through this number</Typography>}
          />
        </DialogContent>
      )}

      <DialogActions sx={{px: 3, pb: 2.5, pt: 1, gap: 1}}>
        {isEdit && (
          <Button sx={{...buttonSx("danger"), mr: "auto"}} onClick={remove} disabled={busy || loading}
                  startIcon={deleting ? <CircularProgress size={14} sx={{color: "#C73A3A"}}/> : null}>
            Disconnect
          </Button>
        )}
        <Button sx={buttonSx("light")} onClick={onClose} disabled={busy}>Cancel</Button>
        <Button sx={buttonSx("dark")} onClick={save} disabled={busy || loading || !valid}
                startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
